import { useState, useEffect, useMemo } from "react";

/**
 * Custom hook to fetch and manage document data
 * @param {Object} pedimentoData - Object containing pedimento information
 * @returns {Object} Document data and utility functions
 */
const useDocuments = (pedimentoData) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [groupFilter, setGroupFilter] = useState("");
  const [sortField, setSortField] = useState("UploadDate");
  const [sortDirection, setSortDirection] = useState("desc");

  // Extract pedimento details
  const pedimentoDetails = useMemo(() => {
    if (!pedimentoData) return null;

    const { pedimento_entry_point, pedimento_license, pedimento_number } =
      pedimentoData || {};
    if (!pedimento_entry_point || !pedimento_license || !pedimento_number) {
      return null;
    }

    return {
      entryPoint: pedimento_entry_point,
      license: pedimento_license,
      number: pedimento_number,
    };
  }, [pedimentoData]);

  // Fetch documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!pedimentoDetails) {
        setError("Missing required pedimento information");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { entryPoint, license, number } = pedimentoDetails;
        const url = `https://wisdomconnect-uat.herokuapp.com/api/generalCatalogue/slamdigital/expediente/find/${entryPoint}/${license}/${number}`;
        const accessToken = localStorage.getItem("accessToken");

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({}),
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setDocuments(data);

        // Automatically expand groups if there are fewer than 5
        const prefixGroups = [
          ...new Set(data.map((doc) => doc.Prefijo || "Other")),
        ];
        if (prefixGroups.length <= 5) {
          const expanded = {};
          prefixGroups.forEach((prefix) => {
            expanded[prefix] = true;
          });
          setExpandedGroups(expanded);
        }
      } catch (err) {
        console.error("Error fetching documents:", err);
        setError(err.message || "Failed to fetch documents");
      } finally {
        setLoading(false);
      }
    };

    if (pedimentoDetails) {
      fetchDocuments();
    }
  }, [pedimentoDetails]);

  // Group documents by prefix
  const groupedDocuments = useMemo(() => {
    return documents.reduce((groups, doc) => {
      const prefix = doc.Prefijo || "Other";
      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      groups[prefix].push(doc);
      return groups;
    }, {});
  }, [documents]);

  // Get unique prefixes for filtering
  const prefixes = useMemo(() => {
    return Object.keys(groupedDocuments).sort();
  }, [groupedDocuments]);

  // Filtered and sorted groups
  const filteredAndSortedGroups = useMemo(() => {
    // First filter by group filter
    const filteredByGroup = Object.entries(groupedDocuments).filter(
      ([prefix]) => !groupFilter || prefix === groupFilter
    );

    // Then filter by search term
    const filteredBySearch = filteredByGroup.filter(([prefix, files]) => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      if (prefix.toLowerCase().includes(searchLower)) return true;

      return files.some(
        (file) =>
          (file.Note && file.Note.toLowerCase().includes(searchLower)) ||
          (file.Ext && file.Ext.toLowerCase().includes(searchLower)) ||
          (file.UploadDate &&
            file.UploadDate.toLowerCase().includes(searchLower))
      );
    });

    // Then sort files within each group
    return filteredBySearch.map(([prefix, files]) => [
      prefix,
      [...files].sort((a, b) => {
        let valueA = a[sortField] || "";
        let valueB = b[sortField] || "";

        if (sortField === "UploadDate") {
          valueA = new Date(valueA || 0);
          valueB = new Date(valueB || 0);
        }

        if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
        if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }),
    ]);
  }, [groupedDocuments, groupFilter, searchTerm, sortField, sortDirection]);

  // Toggle file selection
  const toggleFileSelection = (file) => {
    setSelectedFiles((prev) => {
      const fileId = `${file.Prefijo}-${file.Note}-${file.Ext}`;
      const isSelected = prev.some(
        (f) => `${f.Prefijo}-${f.Note}-${f.Ext}` === fileId
      );

      if (isSelected) {
        return prev.filter((f) => `${f.Prefijo}-${f.Note}-${f.Ext}` !== fileId);
      } else {
        return [...prev, file];
      }
    });
  };

  // Check if a file is selected
  const isFileSelected = (file) => {
    const fileId = `${file.Prefijo}-${file.Note}-${file.Ext}`;
    return selectedFiles.some(
      (f) => `${f.Prefijo}-${f.Note}-${f.Ext}` === fileId
    );
  };

  // Toggle group expansion
  const toggleGroup = (prefix) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [prefix]: !prev[prefix],
    }));
  };

  // Utility functions for document stats
  const documentStats = useMemo(() => {
    const totalDocuments = documents.length;
    const totalGroups = prefixes.length;

    // Count docs by extension
    const extensionCounts = documents.reduce((counts, doc) => {
      const ext = (doc.Ext || "").toLowerCase();
      counts[ext] = (counts[ext] || 0) + 1;
      return counts;
    }, {});

    // Calculate latest upload date
    let latestUpload = null;
    documents.forEach((doc) => {
      if (doc.UploadDate) {
        const uploadDate = new Date(doc.UploadDate);
        if (!latestUpload || uploadDate > latestUpload) {
          latestUpload = uploadDate;
        }
      }
    });

    return {
      totalDocuments,
      totalGroups,
      extensionCounts,
      latestUpload,
    };
  }, [documents, prefixes]);

  return {
    // Data
    documents,
    loading,
    error,
    searchTerm,
    expandedGroups,
    selectedFiles,
    groupFilter,
    sortField,
    sortDirection,
    prefixes,
    filteredAndSortedGroups,
    documentStats,

    // Setters
    setSearchTerm,
    setGroupFilter,
    setSortField,
    setSortDirection,

    // Actions
    toggleFileSelection,
    isFileSelected,
    toggleGroup,
  };
};

export default useDocuments;
